import { zodResolver } from '@hookform/resolvers/zod'
import to from 'await-to-js'
import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import {
  extensionZodValidator,
  phoneNumberZodValidor,
} from 'utils/zodValidators'
import { z } from 'zod'
import { ContactCompany } from '../../../models/companies'
import {
  ContactCreateRequest,
  ContactResponse,
  Department,
  Seniority,
} from '../../../models/contacts'
import { handleFormError } from '../../../utils/handleFormError'
import { Ds2026ButtonProvider } from '../../FbUI/Ds2026ButtonContext'
import {
  DS2026_MODAL_BUTTON_CLASS,
  DS2026_MODAL_CONTENT_CLASS,
  DS2026_MODAL_FOOTER_CLASS,
} from '../../FbUI/Ds2026Field'
import { FbLink } from '../../FbUI/FbLink'
import { Modal } from '../../UI/Modal/Modal'
import useApiService from 'services/api'
import { queryClient } from 'services/queryClient'
import ContactDetailsStep from './ContactDetailsStep'
import {
  checkDoorSelectionRules,
  SelectDoorsStep,
  selectDoorsStepFormSchema,
} from 'components/Deals/CreateDealModal/SelectDoorsStep'

export interface ContactDefaultValues extends ContactCreateRequest {
  company_domain?: string
  consent_to_contact: boolean
}

export const getNewContactFormShape = (
  forCompany?: Partial<ContactCompany>,
  shouldValidateDoorSelection?: boolean
) => {
  const newContactFormShape = z
    .object({
      first_name: z.string().refine((v) => v, {
        message: 'Missing first name',
      }),
      last_name: z.string().refine((v) => v, {
        message: 'Missing last name',
      }),
      contact_company: z.object({
        name: z.string(),
        id: z.number(),
        domain: z.string().nullable().optional(),
      }),
      // Consent is no longer collected in the UI, but the field is still a
      // required part of ContactCreateRequest, so it's submitted as `true`
      // (the value every created contact previously carried). See the `true`
      // default below.
      consent_to_contact: z.boolean(),
      company_domain: forCompany
        ? z.any().optional() // Ignore domain for existing companies
        : z
            .string()
            .trim()
            .optional()
            .refine(
              (v) => {
                if (!v) return true
                // Match regex for domain https://stackoverflow.com/questions/10306690/what-is-a-regular-expression-which-will-match-a-valid-domain-name-without-a-subd
                const domainRegex = new RegExp(
                  /^(((?!-))(xn--|_)?[a-z0-9-]{0,61}[a-z0-9]{1,1}\.)*(xn--)?([a-z0-9][a-z0-9]{0,60}|[a-z0-9-]{1,30}\.[a-z]{2,})$/
                )
                // Test if domain is valid and has at least one dot
                return domainRegex.test(v) && v.includes('.')
              },
              {
                message: 'Enter domain as: domain.com',
              }
            ),
      category_id: z.number().optional(),
      sub_category_id: z.number().optional(),
      email_work: z
        .union([z.string().email(), z.literal('')])
        .nullable()
        .optional(),
      email_personal: z
        .union([z.string().email(), z.literal('')])
        .nullable()
        .optional(),
      email_generic: z
        .union([z.string().email(), z.literal('')])
        .nullable()
        .optional(),
      url: z.union([
        z.literal(''),
        z.string().trim().url('Enter url as: https://www.domain.com'),
      ]),
      linkedin_url: z
        .union([
          z.literal(''),
          z.null(),
          z
            .string()
            .trim()
            .url('URL should start with: https://www.linkedin.com/')
            .refine(
              (v) => {
                if (!v) {
                  return true
                }

                if (!v.startsWith('https://www.linkedin.com/')) {
                  return false
                }

                return true
              },
              { message: 'URL should start with: https://www.linkedin.com/' }
            ),
        ])
        .nullable()
        .optional(),
      notes: z.string().optional(),
      title: z
        .string()
        .optional()
        .nullable()
        .transform((v) => v || ''),
      tags: z.array(z.number()).optional(),
      extension: extensionZodValidator,
      phone_number: z.union([phoneNumberZodValidor, z.null()]),
      seniority: z.nativeEnum(Seniority).nullable().optional(),
      department: z.nativeEnum(Department).nullable().optional(),
    })
    .partial()
    .merge(selectDoorsStepFormSchema)
    .superRefine((v, ctx) => {
      if (!v) return

      const isNewCompany = v.contact_company?.id === -1

      if (isNewCompany) {
        if (!v.company_domain) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Domain is required to create a new company',
            path: ['company_domain'],
          })
        }
        if (!v.category_id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Company category is required for new companies',
            path: ['category_id'],
          })
        }
        if (!v.sub_category_id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Company type is required for new companies',
            path: ['sub_category_id'],
          })
        }
      }

      // Require a phone number OR at least one email. This matches the CSV
      // importer, which accepts phone-only contacts (FIR-5972).
      const hasEmail = !!(v.email_work || v.email_personal || v.email_generic)
      const hasPhone = !!v.phone_number
      if (!hasEmail && !hasPhone) {
        const message = 'Enter a phone number or email'
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ['email_work'],
        })
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ['phone_number'],
        })
      }

      // Only validate door selection if we're on step 2 or submitting
      if (shouldValidateDoorSelection) {
        checkDoorSelectionRules(v, ctx)
      }
    })

  return newContactFormShape
}

export default function ContactModal({
  show,
  handleClose,
  onSubmitSuccess,
  submitApiAction,
  contactToEdit,
  confirmButtonText = 'Create',
  forCompany,
  initialDoorIds,
  initialNotes,
  ds2026 = false,
}: {
  show: boolean
  handleClose: () => void
  onSubmitSuccess?: () => void
  submitApiAction: (values: ContactDefaultValues) => Promise<unknown>
  contactToEdit?: ContactResponse
  confirmButtonText?: string
  forCompany?: Partial<ContactCompany>
  initialDoorIds?: number[]
  /**
   * Prefill for the Notes field when editing. The contact read shape doesn't
   * carry the note, so the Edit flow fetches it separately (contact detail) and
   * passes it here; without it the Notes field reopens blank and a saved note
   * looks lost (it was persisted but never read back).
   */
  initialNotes?: string
  /**
   * Opt this instance into the DS2026 redesign (new field components + DS2026
   * footer buttons). Currently passed only by the Add Contact flow
   * (NewContactModal) so the redesign is gated to that modal while it's
   * evaluated; the Edit flow keeps the existing look.
   */
  ds2026?: boolean
}) {
  // Determine if door selection step should be included
  // When editing a contact, always show door selection if there's a company
  // When creating a contact, show door selection if there's a company context
  const includeDoorSelection = !!forCompany || !!contactToEdit
  const totalSteps = includeDoorSelection ? 2 : 1
  const [currentStep, setCurrentStep] = useState(1)
  const [shouldValidateDoorSelection, setShouldValidateDoorSelection] =
    useState(false)

  const newContactFormShape = useMemo(() => {
    return getNewContactFormShape(forCompany, shouldValidateDoorSelection)
  }, [forCompany, shouldValidateDoorSelection])

  const defaultValues = useCallback(() => {
    if (!contactToEdit) {
      return {
        first_name: '',
        last_name: '',
        consent_to_contact: true,
        url: '',
        linkedin_url: '',
        notes: '',
        title: '',
        seniority: undefined,
        department: undefined,
        tags: [],
        contact_company: {} as ContactDefaultValues['contact_company'],
        company_domain: '',
        email_work: '',
        email_personal: '',
        email_generic: '',
        phone_number: '',
        extension: '',
        apply_to:
          initialDoorIds && initialDoorIds.length > 0
            ? 'select_locations'
            : 'entire_company',
        door_ids: initialDoorIds,
      } as ContactDefaultValues
    }

    // When editing, determine apply_to based on whether door_ids exist
    const hasInitialDoorIds = initialDoorIds && initialDoorIds.length > 0
    return {
      ...contactToEdit,
      // Override with door selection fields AFTER spreading contactToEdit
      // to ensure apply_to and door_ids are correctly set based on initialDoorIds
      apply_to: hasInitialDoorIds ? 'select_locations' : 'entire_company',
      door_ids: initialDoorIds,
      // Keep the original contact_company for editing (needed for selectedContactCompanyId)
      contact_company: contactToEdit.contact_company as any,
      company_domain: contactToEdit?.contact_company?.domain,
      tags: contactToEdit?.tags?.map((tag) => tag.id),
      // Prefill the note (contactToEdit doesn't carry it — see initialNotes).
      notes: initialNotes ?? '',
    } as ContactDefaultValues
  }, [contactToEdit, initialDoorIds, initialNotes])

  const formMethods = useForm({
    defaultValues: defaultValues(),
    // DS2026: validate on blur (and re-validate on blur), so field errors only
    // appear once you leave a field — not on every keystroke while typing.
    // Submit still runs a full trigger(), so nothing is skipped. Legacy keeps
    // the original onChange behavior.
    mode: ds2026 ? 'onBlur' : 'onChange',
    resolver: zodResolver(newContactFormShape),
    reValidateMode: ds2026 ? 'onBlur' : 'onChange',
    criteriaMode: 'all',
    shouldUnregister: false,
  })

  const {
    handleSubmit,
    formState: { isSubmitting },
    setError,
    reset,
    watch,
    trigger,
  } = formMethods

  const _handleClose = () => {
    reset()
    handleClose()
  }

  const onSubmit = handleSubmit(
    async (values) => {
      // Ensure door selection validation is enabled for final submission
      if (!shouldValidateDoorSelection && includeDoorSelection) {
        setShouldValidateDoorSelection(true)
        // Re-trigger validation
        const isValid = await formMethods.trigger()
        if (!isValid) {
          return
        }
      }

      // Transform only the email fields from empty strings to null
      const transformedEmails = {
        email_work: values.email_work === '' ? null : values.email_work,
        email_personal:
          values.email_personal === '' ? null : values.email_personal,
        email_generic:
          values.email_generic === '' ? null : values.email_generic,
      }

      // Merge transformed emails with the rest of the form values
      const transformedValues: ContactDefaultValues = {
        ...values,
        ...transformedEmails,
      }

      const [err] = await to(submitApiAction(transformedValues))
      if (err) {
        console.warn(err)
        const keys = Object.keys(err)
        // If the error does not include `door_ids`, or `apply_to`, it should go back to the 1st step
        if (!keys.includes('door_ids') && !keys.includes('apply_to')) {
          setCurrentStep(1)
          setShouldValidateDoorSelection(false)
        }
        handleFormError(err, setError)
        return
      }

      // Contact create/edit can change which campaigns the contact qualifies
      // for, the sendable email, and the QA classification — all of which
      // feed the sequence-wizard recipient counts. Invalidate every active
      // lead-review query so the sidebar/preview/launch counts converge
      // immediately instead of drifting until launch time (FIR-7242).
      void queryClient.invalidateQueries({
        queryKey: ['campaign-lead-review'],
      })

      // The edit form prefills Notes (and door_ids) from the cached contact
      // detail, which is held with refetchOnMount: false. Without this, editing
      // → Save → reopening in the same session reuses the pre-save cache and the
      // note reads back blank — the exact bug this fix targets.
      if (contactToEdit?.id) {
        void queryClient.invalidateQueries({
          queryKey: ['contact', contactToEdit.id],
        })
      }

      _handleClose()
      onSubmitSuccess?.()
    },
    (err) => console.warn(err)
  )

  const formContactCompany = watch('contact_company')

  const selectedContactCompanyId = useMemo(() => {
    if (formContactCompany) {
      return formContactCompany.id
    }
    return contactToEdit?.contact_company?.id
  }, [formContactCompany, contactToEdit])

  const api = useApiService()
  const { data: { count: companyTotalDoors } = { count: 0 } } =
    api.useGetDoorsForSelectionCount(selectedContactCompanyId ?? 0)

  const handleCancel = useCallback(() => {
    if (currentStep > 1) {
      // Going back to step 1 - disable door selection validation
      if (currentStep === 2) {
        setShouldValidateDoorSelection(false)
        // Trigger validation to clear any door selection errors
        setTimeout(() => {
          formMethods.trigger()
        }, 0)
      }
      setCurrentStep((prev) => prev - 1)
      return
    }
    _handleClose()
  }, [currentStep, _handleClose, formMethods])

  const handleNext = useCallback(async () => {
    // Validate current step before advancing
    const stepValid = await trigger()
    if (!stepValid) return

    // Moving to step 2 - enable door selection validation
    if (currentStep === 1 && totalSteps === 2) {
      setShouldValidateDoorSelection(true)
      // Trigger validation after enabling it
      setTimeout(() => {
        formMethods.trigger()
      }, 0)
    }

    setCurrentStep((s) => s + 1)
  }, [currentStep, totalSteps, formMethods, trigger])

  const modal = (
    <Modal
      open={show}
      onOpenChange={(open) => !open && _handleClose()}
      title={
        ds2026 ? (
          <span className="text-neutral-800">
            {contactToEdit ? 'Edit Contact' : 'New Contact'}
          </span>
        ) : contactToEdit ? (
          'Edit Contact'
        ) : (
          'New Contact'
        )
      }
      description={
        <span className={ds2026 ? 'text-neutral-500' : undefined}>
          Contacts match to companies based on their domain. See{' '}
          {ds2026 ? (
            <Link
              to="/terms-of-use"
              target="_blank"
              className="font-medium text-brand-bright no-underline transition-colors hover:text-brand-active hover:no-underline"
            >
              Terms of Use
            </Link>
          ) : (
            <FbLink to="/terms-of-use" target="_blank">
              Terms of Use
            </FbLink>
          )}
          .
        </span>
      }
      loading={isSubmitting}
      onAccept={
        currentStep === totalSteps
          ? async () => {
              await trigger()
              await onSubmit()
            }
          : handleNext
      }
      onCancel={handleCancel}
      preventCloseOnCancel={true}
      acceptButtonText={currentStep === totalSteps ? confirmButtonText : 'Next'}
      cancelButtonText={currentStep > 1 ? 'Back' : 'Cancel'}
      cancelButtonProps={{
        type: 'button',
        ...(ds2026 ? { className: DS2026_MODAL_BUTTON_CLASS } : {}),
      }}
      acceptButtonProps={
        ds2026 ? { className: DS2026_MODAL_BUTTON_CLASS } : undefined
      }
      // DS2026 footer: 16px gap between the buttons (default is ~7px).
      dialogFooterClassName={ds2026 ? DS2026_MODAL_FOOTER_CLASS : undefined}
      size={'lg'}
      blockAccept={false}
      // DS2026 modal chrome (padding + rounded corners). Single source of truth
      // lives in Ds2026Field; scoped to the add flow via contentClassName.
      contentClassName={ds2026 ? DS2026_MODAL_CONTENT_CLASS : undefined}
      // DS2026 close icon: rounded-lg (rectangle) hover, not a circle; hover bg
      // neutral-100 is already the base, hover icon → neutral-500.
      closeIconClassName={
        ds2026 ? 'rounded-lg hover:text-neutral-500' : undefined
      }
    >
      <FormProvider {...formMethods}>
        {currentStep === 1 && (
          <ContactDetailsStep
            contactToEdit={contactToEdit}
            forCompany={forCompany}
            ds2026={ds2026}
          />
        )}
        {currentStep === 2 && (
          <SelectDoorsStep
            chainProxyId={selectedContactCompanyId!}
            companyTotalDoors={companyTotalDoors}
            radioGroupLabel="Apply this contact to:"
          />
        )}
      </FormProvider>
    </Modal>
  )

  // DS2026 footer buttons (FbButton reads this context) for the gated add flow.
  return ds2026 ? <Ds2026ButtonProvider>{modal}</Ds2026ButtonProvider> : modal
}
